import React, { useCallback, useMemo } from "react";

import "./TimePicker.scss";
import TimeInput from "./TimeInput";
import { Time } from "./Time";

interface TimePickerProps {
  onChange: (time: Time) => void;
  value?: Time;
  withHour?: boolean;
  withMinute?: boolean;
  withSecond?: boolean;
  disabled?: boolean;
  showDefaultTime?: boolean;
}

export default function TimePicker({
  onChange,
  value,
  withHour = true,
  withMinute = true,
  withSecond = true,
  disabled,
  showDefaultTime,
}: TimePickerProps) {
  const time: Time = useMemo(() => value ?? new Time(), [value]);

  const handleHourChange = useCallback(
    (hour: number) => {
      if (disabled) return;

      onChange(time.updateHour(hour));
    },
    [disabled, onChange, time]
  );

  const handleMinuteChange = useCallback(
    (minute: number) => {
      if (disabled) return;

      onChange(time.updateMinute(minute));
    },
    [disabled, onChange, time]
  );

  const handleSecondChange = useCallback(
    (second: number) => {
      if (disabled) return;

      onChange(time.updateSecond(second));
    },
    [disabled, onChange, time]
  );

  return (
    <div className="time-picker">
      {withHour && (
        <>
          <TimeInput
            name="hour"
            value={time.hour}
            min={0}
            max={23}
            onChange={handleHourChange}
            disabled={disabled}
            showDefault={showDefaultTime}
            placeholder="hh"
          />
          <div className="separator">:</div>
        </>
      )}
      {withMinute && (
        <>
          <TimeInput
            name="minute"
            value={time.minute}
            min={0}
            max={withHour ? 59 : 999}
            onChange={handleMinuteChange}
            disabled={disabled}
            showDefault={showDefaultTime}
            placeholder="mm"
          />
          <div className="separator">:</div>
        </>
      )}
      {withSecond && (
        <TimeInput
          name="second"
          value={time.second}
          min={0}
          max={59}
          onChange={handleSecondChange}
          disabled={disabled}
          showDefault={showDefaultTime}
          placeholder="ss"
        />
      )}
    </div>
  );
}
